import React from "react";
import { cn } from "../../lib/utils";

// Main Sidebar Container
interface SidebarProps {
  children: React.ReactNode;
  className?: string;
  width?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({ 
  children, 
  className, 
  width = 256 
}) => {
  return (
    <aside
      className={cn(
        "flex flex-col border-r border-border bg-card/50",
        "sticky top-0 h-screen overflow-hidden",
        className
      )}
      style={{ width: `${width}px` }}
    >
      {children}
    </aside>
  );
};

// Sidebar Header (Logo area)
interface SidebarHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({ 
  children, 
  className 
}) => {
  return (
    <div
      className={cn(
        "flex items-center h-16 px-6 border-b border-border",
        className
      )}
    >
      {children}
    </div>
  );
};

// Sidebar Content (scrollable navigation area)
interface SidebarContentProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarContent: React.FC<SidebarContentProps> = ({ 
  children, 
  className 
}) => {
  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto overflow-x-hidden",
        "py-4",
        className
      )}
    >
      {children}
    </div>
  );
};

// Sidebar Footer (company switcher area)
interface SidebarFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarFooter: React.FC<SidebarFooterProps> = ({ 
  children, 
  className 
}) => {
  return (
    <div
      className={cn(
        "border-t border-border p-4",
        className
      )}
    >
      {children}
    </div>
  );
};

// Sidebar Navigation Item
interface SidebarNavItemProps {
  children: React.ReactNode;
  className?: string;
  active?: boolean;
  onClick?: () => void;
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ 
  children, 
  className,
  active = false,
  onClick
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center w-full px-6 py-2.5 text-sm font-medium",
        "transition-colors duration-150 ease-in-out",
        "hover:bg-accent hover:text-accent-foreground",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        active 
          ? "bg-accent text-accent-foreground" 
          : "text-muted-foreground",
        className
      )}
    >
      {children}
    </button>
  );
};

// Sidebar Section (for grouping navigation items)
interface SidebarSectionProps {
  children: React.ReactNode;
  className?: string;
  title?: string;
}

export const SidebarSection: React.FC<SidebarSectionProps> = ({ 
  children, 
  className,
  title
}) => {
  return (
    <div className={cn("mb-6", className)}>
      {title && (
        <h3 className="px-6 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h3>
      )}
      <nav className="space-y-1">
        {children}
      </nav>
    </div>
  );
};